import { useMemo } from 'react';
import { useTranslator } from '@ianesthesie-kit/core';
import type { Surgery } from './model/surgery';
import type { SurgeryInfo, SurgerySide } from './surgeryInfo/surgeryInfo.model';
import { useUnifiedSurgeries } from './useUnifiedSurgeries';

type Translate = (key: string) => string;

/**
 * A surgery's display name: the typed text for user-defined surgeries (see `useUnifiedSurgeries`),
 * else the hospital's local name, else the translated catalog name.
 */
export const translateSurgery = (
  surgery: Surgery,
  translate: Translate
): string => {
  if (surgery.id.startsWith('USER_DEFINED_')) {
    return surgery.code;
  }
  return surgery.localFacingName?.trim() || translate(surgery.translationCode);
};

/** Surgery names in one string, or a dash when there is none. */
export const joinSurgeryNames = (names: string[]): string =>
  names.join(' / ') || '—';

// The sides a quote or a row names; the others (none, robotic) have no label
const LABELLED_SIDES = new Set<SurgerySide>(['LEFT', 'RIGHT', 'BOTH']);

/** A surgery side's translated label; undefined for sides that have none (unset, none, robotic). */
export const surgerySideLabel = (
  side: SurgerySide | undefined,
  translate: Translate
): string | undefined =>
  side && LABELLED_SIDES.has(side)
    ? translate(`doctor-dashboard.medical.surgerySideSelect.${side}`)
    : undefined;

/** A surgery info's surgery names, in order. */
export const useSurgeryInfoNames = (
  surgeryInfo: SurgeryInfo | undefined
): string[] => {
  const translate = useTranslator();
  const surgeries = useUnifiedSurgeries(surgeryInfo);

  return useMemo(
    () => surgeries.map((s) => translateSurgery(s, translate)),
    [surgeries, translate]
  );
};

/** A surgery info's surgery names joined in one string (a dash when none) and its side label. */
export const useSurgeryInfoLabel = (
  surgeryInfo: SurgeryInfo | undefined
): { surgeries: string; side?: string } => {
  const translate = useTranslator();
  const names = useSurgeryInfoNames(surgeryInfo);
  const side = surgeryInfo?.surgerySide;

  return useMemo(
    () => ({
      surgeries: joinSurgeryNames(names),
      side: surgerySideLabel(side, translate)
    }),
    [names, side, translate]
  );
};
